import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from "react-i18next";

interface SpeakerItem {
  name: string;
  confirm: string;
  organization: string;
  image: string;
  category: 'gov' | 'univ' | 'figure';
  // Diisi kalau pembicara berhalangan hadir dan diwakilkan oleh orang lain
  representedBy?: {
    name: string;
    role: string;
  };
}

const Speakers: React.FC = () => {
  const { t, i18n } = useTranslation('public');
  const isID = i18n.language?.startsWith('id');

  const [activeTab, setActiveTab] = useState<'all' | 'gov' | 'univ' | 'figure'>('all');

  // Menggunakan useMemo agar array data tetap sinkron dengan perubahan bahasa
  const listSpeakers = useMemo<SpeakerItem[]>(() => [
    {
      name: 'Prof. Brian Yuliarto, Ph.D.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.mendikti'),
      image: '/speakers/mendikti-saintek-prof-brian-yuliarto-ph-d.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Abdul Mu\'ti, M.Ed.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.mendikdasmen'),
      image: '/speakers/mendikdasmen-prof-dr-abdul-mu_ti-m-ed.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Khairul Munadi, S.T., M.Eng',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.dirjen_dikti'),
      image: '/speakers/direktur-jenderal-pendidikan-tinggi-prof-dr-khairul-munadi-s-t-m-eng.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Dr. Ahmad Najib Burhani, M.A.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.dirjen_saintek'),
      image: '/speakers/dirjen-sains-dan-teknologi-dr-ahmad-najib-burhani-m-a.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Dr. Berry Juliandi, M.Si.',
      confirm: t('speakers.status_absent'),
      organization: t('speakers.roles.dir_belmawa'),
      image: '/speakers/direktorat-pembelajaran-dan-kemahasiswaan-dirbelmawa-dr-berry-juliandi-m-si.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Muhammad Najib, S.TP., M.M.',
      confirm: t('speakers.status_absent'),
      organization: t('speakers.roles.dir_kelembagaan'),
      image: '/speakers/dirkelembagaan-prof-dr-muhammad-najib-s-tp-m.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Nunuk Suryani, M.Pd.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.dirjen_gtk'),
      image: '/speakers/direktur-jenderal-guru-dan-tenaga-kependidikan-kementerian-pendidikan-kebudayaan-riset-dan-teknologi-dirjen-gtkp-prof-dr-nunuk-suryani-m-pd.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Gogot Suharwoto, S.Pd., M.Ed., Ph.D.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.dirjen_paud'),
      image: '/speakers/direktur-jenderal-pendidikan-anak-usia-dini-pendidikan-dasar-dan-pendidikan-menengah-dirjen-paud-dikdas-dikmen-gogot-suharwoto-s-pd-m-ed-ph-d.jpg',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Ir. Eduart Wolok, ST., MT., IPU., ASEAN.Eng',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.chairman_mrptn'),
      image: '/speakers/ketua-majelis-rektor-perguruan-tinggi-negeri-mrptn-indonesia-prof-dr-ir-eduart-wolok-st-mt.jpg',
      category: 'univ' as const,
    },
    {
      name: 'Prof. Dr. Garuda Wiko, S.H.,M.Si.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.chairman_fri'),
      image: '/speakers/garuda-wiko.png',
      category: 'univ' as const,
    },
    {
      name: 'Prof. Dr. I Wayan Lasmawan, M.Pd.',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.chairman_lptk'),
      image: '/speakers/rektor_undiksha.jpg',
      category: 'univ' as const,
    },
    {
      name: 'Prof. Dr. H. Didi Sukyadi, M.A.',
      confirm: t('speakers.status_absent'),
      organization: t('speakers.roles.rector_upi'),
      image: '/speakers/rektor_upi.jpg',
      category: 'univ' as const,
    },

    {
      name: 'Prof. Dr. Haedar Nashir, M.Si',
      confirm: t('speakers.status_confirmed'),
      organization: t('speakers.roles.chairman_muhammadiyah'),
      image: '/speakers/ketua-umum-pimpinan-pusat-muhammadiyah-ke-15-prof-dr-haedar-nashir-m-si.jpg',
      category: 'figure' as const,
      representedBy: {
        name: 'Prof. Dr. Muhadjir Effendy',
        role: t('speakers.roles.deputy_chairman_muhammadiyah'),
      },
    },

    {
      name: 'Fadli Zon, S.S., M.Sc.',
      confirm: t('speakers.status_absent'),
      organization: isID ? 'Menteri Kebudayaan Republik Indonesia' : 'Minister of Culture of the Republic of Indonesia',
      image: '/speakers/fadli-zon.png',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Ir. Heri Hermansyah, S.T., M.Eng., IPU',
      confirm: t('speakers.status_confirmed'),
      organization: isID ? 'Rektor Universitas Indonesia' : 'Rector of Universitas Indonesia',
      image: '/speakers/prof-Dr-Ir-Heri-Hermansyah.png',
      category: 'univ' as const,
    },
    {
      name: 'Prof. Drs. Ganefri, M.Pd., Ph.D.',
      confirm: t('speakers.status_confirmed'),
      organization: isID ? 'Senior Eksekutif UNP' : 'Senior Executive of UNP',
      image: '/speakers/ganefri.png',
      category: 'univ' as const,
    },
    {
      name: 'Prof. Dr. AH. Rofi’uddin, M.Pd.',
      confirm: t('speakers.status_confirmed'),
      organization: isID ? 'Tokoh Pendidikan' : 'Education Figure',
      image: '/speakers/rofiuddin.png',
      category: 'univ' as const,
    },
    {
      name: 'Ferry Maulana Putra, S.Pd., M.Ed.',
      confirm: t('speakers.status_confirmed'),
      organization: isID ? 'Direktur PPG' : 'Director of PPG',
      image: '/speakers/ferry-maulana-putra.png',
      category: 'gov' as const,
    },
    {
      name: 'Ir. Moch. Abduh, MS.Ed., Ph.D.',
      confirm: t('speakers.status_confirmed'),
      organization: isID ? 'Staf Ahli Menteri Bidang Teknologi Pendidikan' : 'Expert Staff to the Minister for Educational Technology',
      image: '/speakers/moch-abduh.png',
      category: 'gov' as const,
    },
    {
      name: 'Prof. Dr. Unifah Rosyidi, M.Pd.',
      confirm: t('speakers.status_absent'),
      organization: isID ? 'Ketua Umum PB PGRI' : 'Chairman of PB PGRI',
      image: '/speakers/unifah-rosyidi.png',
      category: 'figure' as const,
    },
  ], [t]);

  const filteredSpeakers = useMemo(() => {
    if (activeTab === 'all') return listSpeakers;
    return listSpeakers.filter(speaker => speaker.category === activeTab);
  }, [listSpeakers, activeTab]);

  return (
    <section id="speakers" className="py-20 bg-gray-50 dark:bg-gray-800 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-20">

        {/* WELCOME / KEYNOTE SPEAKER - GRAND SPLIT BANNER */}
        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h3 className="text-4xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t('speakers.welcome_title')} <span className="text-primary dark:text-light">{t('speakers.welcome_highlight')}</span>
            </h3>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto bg-gradient-to-br from-white via-slate-50/90 to-blue-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-slate-900/90 rounded-3xl border border-slate-200/90 dark:border-slate-800 shadow-2xl hover:shadow-[0_20px_70px_-15px_rgba(0,119,255,0.18)] transition-all duration-500 overflow-hidden group relative"
          >
            {/* Background Decorative Aura */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/10 via-blue-500/5 to-transparent dark:from-light/10 dark:via-blue-400/5 rounded-full blur-3xl pointer-events-none transform translate-x-1/3 -translate-y-1/3"></div>

            <div className="grid md:grid-cols-12 items-stretch relative z-10">
              {/* Photo Side */}
              <div className="md:col-span-5 h-[380px] md:h-auto min-h-[420px] relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                <img
                  src="/speakers/presiden.jpg"
                  alt="Prabowo Subianto"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-black/70 to-transparent md:hidden pointer-events-none"></div>
                {t('speakers.status_absent') && (
                  <div className="absolute bottom-4 left-4 z-10">
                    <span className="px-3.5 py-1.5 rounded-full bg-gradient-to-r from-slate-600 via-slate-500 to-slate-400 text-white font-bold text-xs tracking-wide shadow-md border border-slate-400/30 inline-flex items-center gap-1.5">
                      {t('speakers.status_absent')}
                    </span>
                  </div>
                )}
              </div>

              {/* Presidential Content Side */}
              <div className="md:col-span-7 p-8 sm:p-12 lg:p-14 flex flex-col justify-center space-y-6">
                <div>
                  {/* Keynote Speaker Badge */}
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 dark:bg-light/10 border border-primary/20 dark:border-light/20 text-primary dark:text-light text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-primary dark:bg-light animate-ping"></span>
                    {isID ? 'Sambutan Utama' : 'Opening Address'}
                  </div>

                  <h3 className="text-3xl sm:text-4xl md:text-[2.6rem] font-extrabold text-gray-900 dark:text-white tracking-tight leading-[1.15] mb-3 group-hover:text-primary dark:group-hover:text-light transition-colors">
                    Prabowo Subianto
                  </h3>

                  {/* Highlighted Role Pill */}
                  <div className="inline-block bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent dark:from-blue-500/20 dark:via-blue-500/10 border-l-4 border-primary dark:border-light px-4 py-2.5 rounded-r-xl">
                    <p className="text-base sm:text-lg font-bold text-primary dark:text-light leading-snug">
                      {t('speakers.roles.president')}
                    </p>
                  </div>
                </div>

                {/* Plenary Session Overview Box (Faktual - Tanpa Kutipan) */}
                {/* <div className="p-5 rounded-2xl bg-white/90 dark:bg-gray-800/80 backdrop-blur-md border border-slate-200/80 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-primary to-blue-600 dark:from-light dark:to-blue-400"></div>
                  <div className="pl-3 space-y-1">
                    <span className="text-xs font-bold text-primary dark:text-light uppercase tracking-wider">
                      {isID ? 'Fokus Sesi Pleno Pembuka' : 'Opening Plenary Focus'}
                    </span>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
                      {t('speakers.description')}
                    </p>
                  </div>
                </div> */}

                {/* Prestigious Bottom Meta-Strip */}
                <div className="pt-2 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-widest border-t border-slate-100 dark:border-slate-800/80">
                  <span>KONASPI XII • 2026</span>
                  {/* <span className="flex items-center gap-1.5 text-primary dark:text-light">
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    {isID ? 'Sesi Pleno Pembuka' : 'Opening Plenary Session'}
                  </span> */}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* MAIN / INVITED SPEAKERS - CATEGORY TABS & FULL-BLEED OVERLAY CARDS */}
        <div className="space-y-10 pt-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-4"
          >
            <h3 className="text-4xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {t('speakers.main_title')} <span className="text-primary dark:text-light">{t('speakers.main_highlight')}</span>
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {t('speakers.description')}
            </p>

            {/* Interactive Category Filter Tabs */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 pt-4">
              {[
                { id: 'all', label: isID ? 'Semua Pembicara' : 'All Speakers' },
                { id: 'gov', label: isID ? 'Kementerian & Dirjen' : 'Government & Leaders' },
                { id: 'univ', label: isID ? 'Rektor & Pimpinan PT' : 'Rectors & Universities' },
                { id: 'figure', label: isID ? 'Tokoh & Pengamat' : 'Figures & Observers' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 shadow-sm ${activeTab === tab.id
                    ? 'bg-primary text-white dark:bg-light dark:text-gray-900 shadow-md scale-105'
                    : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-slate-200/80 dark:border-slate-800 hover:border-primary/50 dark:hover:border-light/50 hover:text-gray-900 dark:hover:text-white'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Speakers Grid with Ultra-Smooth Apple-Style Cascading Transition */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 pt-4"
            >
              {filteredSpeakers.map((speaker, index) => (
                <motion.div
                  key={speaker.name}
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.45,
                    ease: [0.16, 1, 0.3, 1],
                    delay: Math.min(index * 0.04, 0.25),
                  }}
                  className="bg-white dark:bg-gray-900 rounded-3xl overflow-hidden border border-slate-200/80 dark:border-slate-800 shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col group"
                >
                  {/* Photo Box - 100% Bright, Natural, & Crystal Clear */}
                  <div className="relative h-72 sm:h-80 overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={speaker.image}
                      alt={speaker.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                    {/* Subtle bottom gradient ONLY for badge contrast right at the bottom edge (h-16 / 4rem) */}
                    <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-black/65 to-transparent pointer-events-none"></div>

                    {/* Badge positioned right at the bottom-left of the photo */}
                    {speaker.confirm && (
                      <div className="absolute bottom-3 left-3 z-10">
                        <span
                          className={`px-3 py-1 rounded-full text-white font-bold text-xs tracking-wide shadow-md inline-flex items-center gap-1.5 border ${speaker.confirm === t('speakers.status_confirmed') ||
                              speaker.confirm === 'Telah Dikonfirmasi' ||
                              speaker.confirm === 'Confirmed'
                              ? 'bg-gradient-to-r from-emerald-600 via-emerald-500 to-green-500 border-emerald-400/30'
                              : speaker.confirm === t('speakers.status_absent') ||
                                speaker.confirm === 'Tidak Hadir' ||
                                speaker.confirm === 'Not Attending' ||
                                speaker.confirm === 'Berhalangan Hadir' ||
                                speaker.confirm === 'Unable to Attend'
                              ? 'bg-gradient-to-r from-slate-600 via-slate-500 to-slate-400 border-slate-400/30'
                              : 'bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-500 border-amber-400/30'
                            }`}
                        >
                          {speaker.confirm !== t('speakers.status_absent') &&
                           speaker.confirm !== 'Tidak Hadir' &&
                           speaker.confirm !== 'Not Attending' &&
                           speaker.confirm !== 'Berhalangan Hadir' &&
                           speaker.confirm !== 'Unable to Attend' && (
                             <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse shadow-sm"></span>
                          )}
                          {speaker.confirm}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Clean Info Panel Underneath */}
                  <div className="p-6 flex-1 flex flex-col justify-between bg-white dark:bg-gray-900">
                    <div>
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2 leading-snug group-hover:text-primary dark:group-hover:text-light transition-colors">
                        {speaker.name}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed font-medium">
                        {speaker.organization}
                      </p>

                      {speaker.representedBy && (
                        <div className="mt-3 pt-3 border-t border-dashed border-slate-200 dark:border-slate-700">
                          <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">
                            {t('speakers.absent_note')}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                            <span className="text-gray-500 dark:text-gray-400">{t('speakers.represented_by_label')}: </span>
                            <span className="font-bold text-gray-900 dark:text-white">{speaker.representedBy.name}</span>
                            <span className="block text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                              {speaker.representedBy.role}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
};

export default Speakers;